/**
 * Personal pace model: ETAs based on how THIS driver actually drives, not on
 * how fast the road theoretically allows.
 *
 * Mapbox's duration assumes a generic vehicle at roughly the posted limit. A
 * loaded Hino does not do 120 on the motorway, and a driver who cruises at 90
 * gets an ETA that is optimistic on every single leg - the error compounds over
 * a 300 km run. So the driver sets their own cruising speed for three road
 * tiers and we re-derive the journey time from those.
 *
 * How a step's tier is decided
 * ----------------------------
 * The driver's rule is "motorways are the roads with more than 2 lanes". We
 * cannot take that literally from the API: `intersections[].lanes` is TURN-lane
 * data at junctions, so a one-lane soi with a left/straight/right arrangement
 * reports three lanes and would masquerade as a motorway. Road class is the
 * reliable signal and in Thailand it tracks carriageway width closely, so class
 * leads and lane counts are only used to refine within a class - and only when
 * several junctions along the step agree.
 *
 * Traffic still wins when it is worse
 * -----------------------------------
 * Per step we take the SLOWER of (live traffic duration, personal-pace
 * duration). Driving slower than the road allows should push the ETA out; it
 * should never let us claim we'll beat a traffic jam.
 */

import type { Route, RouteStep } from '../core/types';

export type RoadTier = 'motorway' | 'dual' | 'single';

/** Cruising speeds in km/h, per road tier. */
export interface PaceSpeeds {
  /** More than two lanes: motorways, trunk roads, expressways. */
  motorway: number;
  /** Two-lane main roads. */
  dual: number;
  /** Single-lane roads, sois, service roads. */
  single: number;
}

export const DEFAULT_SPEEDS: PaceSpeeds = {
  motorway: 100,
  dual: 80,
  single: 55,
};

const CLASS_TIER: Record<string, RoadTier> = {
  motorway: 'motorway',
  motorway_link: 'motorway',
  trunk: 'motorway',
  trunk_link: 'motorway',
  primary: 'dual',
  primary_link: 'dual',
  secondary: 'dual',
  secondary_link: 'dual',
  tertiary: 'single',
  tertiary_link: 'single',
  street: 'single',
  street_limited: 'single',
  service: 'single',
  track: 'single',
  path: 'single',
  ferry: 'single',
};

/**
 * Which tier a step belongs to. Road class leads; a consistent lane count
 * across the step's junctions can promote or demote it by one tier.
 */
export function tierForStep(step: RouteStep): RoadTier {
  const classes: string[] = [];
  const laneCounts: number[] = [];

  for (const x of step.intersections ?? []) {
    const cls = x.mapbox_streets_v8?.class;
    if (cls) classes.push(cls);
    if (x.lanes?.length) laneCounts.push(x.lanes.length);
  }

  // Most common class along the step.
  let tier: RoadTier = 'single';
  if (classes.length) {
    const tally = new Map<string, number>();
    for (const c of classes) tally.set(c, (tally.get(c) ?? 0) + 1);
    let bestClass = '';
    let best = 0;
    for (const [c, n] of tally) {
      if (n > best) {
        best = n;
        bestClass = c;
      }
    }
    tier = CLASS_TIER[bestClass] ?? 'single';
  }

  // Refine only when at least two junctions agree, since a single junction's
  // turn lanes say nothing about how wide the road actually is.
  if (laneCounts.length >= 2) {
    const sorted = [...laneCounts].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    if (median > 2 && tier === 'dual') tier = 'motorway';
    if (median <= 1 && tier === 'dual') tier = 'single';
  }

  return tier;
}

/** Seconds lost at an average signalised junction. Thai cycles are long. */
export const DEFAULT_SIGNAL_SECONDS = 120;

export interface PaceEstimate {
  /** Seconds, all-in: rolling time plus signal waits, floored by live traffic. */
  duration: number;
  /** Seconds actually rolling, at the driver's own cruising speeds. */
  rollingSeconds: number;
  /** Seconds sat at red lights. */
  signalSeconds: number;
  /** Signalised junctions counted on this route. */
  signals: number;
  /** Metres driven in each tier, for explaining the estimate. */
  distanceByTier: Record<RoadTier, number>;
  /** How much slower (positive) or faster (negative) than Mapbox's estimate. */
  deltaVsRouter: number;
  /** True when live traffic was worse than the model and set the figure. */
  trafficBound: boolean;
}

/**
 * Re-time a route using the driver's own cruising speeds and signal cost.
 *
 * Deliberately built bottom-up from the driver's own numbers - rolling time
 * plus red lights - rather than adding signal time on top of Mapbox's
 * duration, which already contains some of it. Mapbox's traffic-aware figure
 * is then used as a FLOOR: if the road is jammed worse than this model
 * predicts, we trust the jam.
 */
export function estimateWithPace(
  route: Route,
  speeds: PaceSpeeds,
  signalSeconds = DEFAULT_SIGNAL_SECONDS,
): PaceEstimate {
  const distanceByTier: Record<RoadTier, number> = { motorway: 0, dual: 0, single: 0 };
  let rollingSeconds = 0;
  let signals = 0;

  for (const leg of route.legs) {
    for (const step of leg.steps) {
      const tier = tierForStep(step);
      distanceByTier[tier] += step.distance;

      const kmh = Math.max(5, speeds[tier] || DEFAULT_SPEEDS[tier]);
      rollingSeconds += step.distance / (kmh / 3.6);

      for (const x of step.intersections ?? []) {
        if (x.traffic_signal) signals++;
      }
    }
  }

  const signalTotal = signals * Math.max(0, signalSeconds);
  const modelled = rollingSeconds + signalTotal;

  // Steps can be missing on some responses; never return a nonsense zero.
  const base = modelled > 0 ? modelled : route.duration;
  const trafficBound = route.duration > base;
  const duration = Math.max(base, route.duration);

  return {
    duration,
    rollingSeconds,
    signalSeconds: signalTotal,
    signals,
    distanceByTier,
    deltaVsRouter: duration - route.duration,
    trafficBound,
  };
}

/**
 * Attach the pace estimate to each route and re-rank fastest-first.
 *
 * The re-rank is the point: at 100/80/55 km/h with two minutes a light, a
 * motorway route with six signals can beat a "faster" main-road route with
 * thirty, and Mapbox's own ordering will never show that.
 */
export function applyPace(
  routes: Route[],
  speeds: PaceSpeeds,
  signalSeconds = DEFAULT_SIGNAL_SECONDS,
): Route[] {
  for (const r of routes) {
    const est = estimateWithPace(r, speeds, signalSeconds);
    r._paceDuration = est.duration;
    r._paceSignalSeconds = est.signalSeconds;
  }
  return [...routes].sort((a, b) => paceDuration(a) - paceDuration(b));
}

/** The duration to show and rank by: personal pace when we have it. */
export function paceDuration(route: Route): number {
  return route._paceDuration ?? route.duration;
}
